/**
 * Admin Protected Route Component
 *
 * Wraps admin routes that require authentication.
 * Redirects to /admin/login if user is not authenticated (instead of regular /login).
 * Also checks that user has admin role.
 */

import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { LoadingSpinner } from "@/components/common/LoadingSpinner";

// Admin email whitelist (must match AdminLoginPage.jsx and backend)
const ADMIN_EMAILS = [
  "chad@chadpassa.com",
  "lisahart@live.com",
  "admin@vouchee.app",
];

export default function AdminProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  // Show loading state while checking auth
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <LoadingSpinner label="Verifying admin access..." size="lg" />
      </div>
    );
  }

  // Redirect to admin login if not authenticated
  if (!user) {
    return <Navigate to="/admin/login" state={{ from: location }} replace />;
  }

  // Check if user is an admin
  const isAdmin =
    user.account_type === "admin" ||
    ADMIN_EMAILS.includes(user.email?.toLowerCase());

  if (!isAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-900">
        <div className="text-center max-w-md mx-auto p-8 bg-slate-800 rounded-2xl shadow-xl border border-slate-700">
          <div className="text-red-500 text-5xl mb-4">⛔</div>
          <h2 className="text-2xl font-bold text-white mb-2">
            Admin Access Required
          </h2>
          <p className="text-slate-400 mb-6">
            Your account does not have administrator privileges. Please contact
            support if you believe this is an error.
          </p>
          <div className="space-y-3">
            <button
              onClick={() => (window.location.href = "/admin/login")}
              className="w-full px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Login with Admin Account
            </button>
            <button
              onClick={() => (window.location.href = "/")}
              className="w-full px-6 py-3 bg-slate-700 text-slate-300 rounded-lg hover:bg-slate-600 transition-colors"
            >
              Return to Main Site
            </button>
          </div>
        </div>
      </div>
    );
  }

  return children;
}
